// src/components/Tutorial/TutorialLesson.js
import React from "react";
import {
    Box,
    Button,
    Chip,
    Container,
    Divider,
    Grid,
    Paper,
    Stack,
    Typography,
    useMediaQuery
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import ImageIcon from "@mui/icons-material/Image";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SchoolIcon from "@mui/icons-material/School";
import TuneIcon from "@mui/icons-material/Tune";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

const quickPath = [
    "Preencha o formulário",
    "Adicione a BNCC",
    "Gere o material",
    "Revise e exporte"
];

const formFields = [
    {
        label: "Tipo de conteúdo",
        value: "Atividade, Avaliação ou Plano de Aula",
        hint: "Define o formato final."
    },
    {
        label: "Etapa, série e disciplina",
        value: "Ex.: Fundamental II, 6º ano, Matemática",
        hint: "Filtra as opções certas."
    },
    {
        label: "Tema",
        value: "Ex.: frações, célula animal, Revolução Francesa",
        hint: "Seja direto."
    },
    {
        label: "Nível",
        value: "Básico, Intermediário ou Avançado",
        hint: "Ajusta a dificuldade."
    },
    {
        label: "Tipo e quantidade",
        value: "Múltipla escolha, dissertativa, ilustrativa...",
        hint: "Não aparece no Plano de Aula."
    },
    {
        label: "Observações",
        value: "Pedidos extras para a IA",
        hint: "Opcional e curto."
    }
];

const steps = [
    {
        icon: <TuneIcon />,
        title: "1. Comece pelo básico",
        text: "Escolha o tipo de material, etapa, série, disciplina, tema e nível. Esses campos dizem para a IA qual é o contexto da sua turma.",
        tip: "Tema bom: curto e específico. Ex.: multiplicação com números decimais."
    },
    {
        icon: <SchoolIcon />,
        title: "2. Informe a BNCC",
        text: "Depois de escolher etapa, série e disciplina, a plataforma mostra sugestões de códigos BNCC. Você pode selecionar uma sugestão ou digitar seu próprio código e descrição.",
        tip: "Importante: depois de preencher código e descrição, clique em Add BNCC. Sem isso, a BNCC não entra no material."
    },
    {
        icon: <AutoAwesomeIcon />,
        title: "3. Gere o documento",
        text: "Revise os campos e clique em Gerar Documento. A plataforma monta o material com base nas escolhas, na BNCC e nas observações.",
        tip: "Enquanto carrega, aguarde a finalização antes de tentar gerar outro."
    },
    {
        icon: <EditIcon />,
        title: "4. Edite antes de usar",
        text: "O resultado não é travado: você pode clicar nos textos, ajustar enunciados, alternativas, plano de aula e detalhes do cabeçalho.",
        tip: "Leia como professor: corrija possíveis erros na escrita ou qualquer falha pedagógica."
    },
    {
        icon: <ImageIcon />,
        title: "5. Cuide das imagens",
        text: "Em questões ilustrativas, use a descrição abaixo do bloco da imagem como guia. Você pode gerar a imagem com IA (se o seu acesso permitir) ou enviar uma imagem do seu computador.",
        tip: "A descrição ajuda a gerar ou escolher a imagem, mas não sai na exportação."
    },
    {
        icon: <DownloadIcon />,
        title: "6. Exporte",
        text: "Quando estiver tudo pronto, exporte em Word ou PDF. Se houver gabarito e seu plano permitir, exporte o gabarito separadamente.",
        tip: "O arquivo exportado leva o material final, não as dicas visuais da tela."
    }
];

const imageOptions = [
    { icon: <AutoAwesomeIcon />, title: "IA", text: "Gere a imagem com um clique usando a descrição como base." },
    { icon: <UploadFileIcon />, title: "Upload", text: "Envie PNG, JPG ou JPEG que combine com a descrição." },
    { icon: <EditIcon />, title: "Ajuste", text: "Troque, remova ou redimensione antes de exportar." }
];

const whatsappURL = "https://wa.me/5524998359874?text=Ol%C3%A1%2C+fiquei+com+uma+d%C3%BAvida+sobre+como+usar+a+Docen!";

function MiniField({ label, value, hint }) {
    return (
        <Box
            sx={{
                p: 1.4,
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                bgcolor: "background.paper",
                minHeight: 86
            }}
        >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.5, lineHeight: 1.25 }}>
                {value}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                {hint}
            </Typography>
        </Box>
    );
}

function StepItem({ step, index }) {
    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: { xs: "40px 1fr", sm: "48px 1fr" },
                gap: 1.6,
                p: { xs: 1.5, sm: 2 },
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                bgcolor: "background.paper"
            }}
        >
            <Box
                sx={{
                    width: { xs: 40, sm: 48 },
                    height: { xs: 40, sm: 48 },
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "primary.main",
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                    "& .MuiSvgIcon-root": { fontSize: { xs: 22, sm: 25 } }
                }}
            >
                {step.icon}
            </Box>
            <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.7, flexWrap: "wrap" }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                        {step.title}
                    </Typography>
                    {index === 1 && <Chip size="small" label="Obrigatório" color="primary" variant="outlined" />}
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                    {step.text}
                </Typography>
                <Box
                    sx={{
                        mt: 1.2,
                        px: 1.2,
                        py: 0.9,
                        borderRadius: 1,
                        display: "flex",
                        gap: 1,
                        alignItems: "flex-start",
                        bgcolor: (theme) => alpha(theme.palette.info.main, 0.08)
                    }}
                >
                    <InfoOutlinedIcon color="info" sx={{ fontSize: 18, mt: 0.1 }} />
                    <Typography variant="caption" sx={{ lineHeight: 1.45 }}>
                        {step.tip}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

function BnccPreview() {
    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                borderRadius: 1,
                height: "100%",
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04)
            }}
        >
            <Stack spacing={1.4}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <SchoolIcon color="primary" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                        BNCC sem mistério
                    </Typography>
                </Stack>

                <Box sx={{ display: "grid", gap: 1 }}>
                    <Chip label="Sugestão: EF06MA07" color="primary" variant="outlined" sx={{ justifyContent: "flex-start" }} />
                    <Box
                        sx={{
                            p: 1.3,
                            borderRadius: 1,
                            border: (theme) => `1px dashed ${theme.palette.primary.main}`,
                            bgcolor: "background.paper"
                        }}
                    >
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                            Descrição BNCC
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.4 }}>
                            Resolver problemas envolvendo frações e suas representações.
                        </Typography>
                    </Box>
                    <Button startIcon={<AddIcon />} variant="outlined" sx={{ borderRadius: 1, alignSelf: "flex-start" }}>
                        Add BNCC
                    </Button>
                </Box>

                <Typography variant="body2" color="text.secondary">
                    A plataforma sugere, mas você decide. Pode usar a sugestão, alterar a descrição ou cadastrar outra habilidade.
                </Typography>
            </Stack>
        </Paper>
    );
}

function ImagePreview() {
    return (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, height: "100%" }}>
            <Stack spacing={1.5}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <ImageIcon color="primary" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                        Bloco de imagem
                    </Typography>
                </Stack>
                <Box
                    sx={{
                        height: 150,
                        borderRadius: 1,
                        border: "1.8px dashed",
                        borderColor: "primary.main",
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        px: 2
                    }}
                >
                    <Stack alignItems="center" spacing={0.8}>
                        <UploadFileIcon color="primary" />
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 800 }}>
                            Clique para subir a imagem
                        </Typography>
                    </Stack>
                </Box>
                <Box sx={{ p: 1.2, borderRadius: 1, bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12) }}>
                    <Typography variant="caption" sx={{ fontWeight: 800 }}>
                        Descrição da imagem
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.4 }}>
                        "Uma criança dividindo uma pizza em partes iguais."
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.8 }}>
                        Use como orientação. Ela não aparece no PDF ou Word.
                    </Typography>
                </Box>
            </Stack>
        </Paper>
    );
}

export default function TutorialLesson() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "background.default", py: { xs: 2, md: 4 } }}>
            <Container maxWidth="lg">
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2, md: 3 },
                        borderRadius: 1,
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        mb: 2.5
                    }}
                >
                    <Grid container spacing={2.5} alignItems="center">
                        <Grid item xs={12} md={7}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: "wrap" }}>
                                <Chip icon={<DescriptionIcon />} label="Tutorial rápido" color="primary" variant="outlined" />
                            </Stack>
                            <Typography
                                variant={isMobile ? "h4" : "h3"}
                                sx={{ fontWeight: 900, letterSpacing: 0, lineHeight: 1.05, mb: 1.2 }}
                            >
                                Gere seu material em poucos passos
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 680, lineHeight: 1.65 }}>
                                Um guia visual para preencher o formulário, usar a BNCC, revisar textos, cuidar das imagens e exportar o documento final.
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <Stack spacing={1}>
                                {quickPath.map((item, index) => (
                                    <Box
                                        key={item}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.2,
                                            p: 1.1,
                                            borderRadius: 1,
                                            bgcolor: (theme) => alpha(theme.palette.primary.main, index === 1 ? 0.12 : 0.06)
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 26,
                                                height: 26,
                                                borderRadius: "50%",
                                                bgcolor: "primary.main",
                                                color: "primary.contrastText",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontWeight: 800,
                                                fontSize: 13,
                                                flex: "0 0 auto"
                                            }}
                                        >
                                            {index + 1}
                                        </Box>
                                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                            {item}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        </Grid>
                    </Grid>
                </Paper>

                <Grid container spacing={2.5}>
                    <Grid item xs={12} lg={7}>
                        <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 1 }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                <FormatListBulletedIcon color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                                    O que preencher
                                </Typography>
                            </Stack>
                            <Grid container spacing={1.3}>
                                {formFields.map((field) => (
                                    <Grid item xs={12} sm={6} key={field.label}>
                                        <MiniField {...field} />
                                    </Grid>
                                ))}
                            </Grid>
                            <Divider sx={{ my: 2 }} />
                            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                                <InfoOutlinedIcon color="primary" sx={{ mt: 0.2 }} />
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                                    Em Língua Portuguesa podem aparecer opções extras, como interpretação de texto, gênero textual e produção textual. Use apenas quando fizer sentido para a aula.
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} lg={5}>
                        <BnccPreview />
                    </Grid>

                    <Grid item xs={12} lg={7}>
                        <Stack spacing={1.5}>
                            {steps.map((step, index) => (
                                <StepItem key={step.title} step={step} index={index} />
                            ))}
                        </Stack>
                    </Grid>

                    <Grid item xs={12} lg={5}>
                        <Stack spacing={2.5}>
                            <ImagePreview />
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 1.5 }}>
                                    Imagem: escolha o caminho
                                </Typography>
                                <Stack spacing={1.2}>
                                    {imageOptions.map((option) => (
                                        <Box key={option.title} sx={{ display: "flex", gap: 1.2, alignItems: "flex-start" }}>
                                            <Box
                                                sx={{
                                                    color: "primary.main",
                                                    mt: 0.2,
                                                    "& .MuiSvgIcon-root": { fontSize: 20 }
                                                }}
                                            >
                                                {option.icon}
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                                    {option.title}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                                                    {option.text}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                </Stack>
                            </Paper>
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2,
                                    borderRadius: 1,
                                    bgcolor: (theme) => alpha(theme.palette.success.main, 0.08)
                                }}
                            >
                                <Stack direction="row" spacing={1} alignItems="flex-start">
                                    <CheckCircleIcon color="success" />
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                                            Regra de ouro
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                                            A IA adianta o trabalho, mas a revisão final é sua. Ajuste o que precisar antes de aplicar com os alunos.
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Paper>
                        </Stack>
                    </Grid>
                </Grid>

                <Paper
                    elevation={0}
                    sx={{
                        mt: 2.5,
                        p: { xs: 2, md: 2.5 },
                        borderRadius: 1,
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: { xs: "stretch", sm: "center" },
                        justifyContent: "space-between",
                        gap: 1.5
                    }}
                >
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                            Ainda ficou com alguma dúvida?
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Fale com nosso suporte pelo WhatsApp para receber ajuda no uso da plataforma.
                        </Typography>
                    </Box>
                    <Button
                        size="large"
                        variant="contained"
                        color="success"
                        startIcon={<WhatsAppIcon />}
                        href={whatsappURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ borderRadius: 1, px: 2.5, textTransform: "none" }}
                    >
                        Falar com suporte
                    </Button>
                </Paper>
            </Container>
        </Box>
    );
}
